import { ApolloProvider } from '@apollo/client';
import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import Tabs from '@atlaskit/tabs';
import { TabData } from '@atlaskit/tabs/dist/types/types';
import { ProxyGraphClients, abgGraphqlClient } from '@atlassian/temp-client-utils';
import { FetchTaskListQuery, TaskStatus, TaskType, withConfirmationDialog } from '@css-xp/apd-common';
import { ErrorComponents } from '@css-xp/base';
import { useApplicationContext } from '@css-xp/base/common-context';
import { COMPLETED_TASKS_TAB, OPEN_TASKS_TAB } from '../../constants/task';
import { fetchTaskList } from '../../queries/tasks';
import { TaskListTable } from '../task-list-table/task-list-table';
import './all-tasks-list.scss';
import {
  createTableHead as createClosedTasksTableHead,
  createTableRows as createClosedTasksTableRows,
} from './closed-tasks-list-utils';
import {
  createTableHead as createOpenTasksTableHead,
  createTableRows as createOpenTasksTableRows,
} from './open-tasks-list-utils';

interface AllTaskListProps {
  businessKey?: string;
  searchable?: boolean;
  reloadAll?: () => void;
  skip?: boolean;
}

const AllTasksList: React.FC<AllTaskListProps> = forwardRef<any, AllTaskListProps>(
  ({ businessKey, searchable, reloadAll, skip = false }: AllTaskListProps, ref: React.Ref<unknown> | undefined) => {
    const [data, setData] = useState<FetchTaskListQuery | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<Error | null>(null);
    const [refetch, setRefetch] = useState<number>(0);
    const user = useApplicationContext();
    const client = React.useMemo(() => abgGraphqlClient(ProxyGraphClients.APD), []);
    const [taskStatus, setTaskStatus] = useState<TaskStatus>(TaskStatus.Open);

    const head = React.useMemo(() => {
      switch (taskStatus) {
        case TaskStatus.Open:
          return createOpenTasksTableHead(true);
        case TaskStatus.Completed:
          return createClosedTasksTableHead(true);
      }
    }, [taskStatus]);

    const _refetch = React.useCallback(() => setRefetch((r) => r + 1), []);

    const visibleRows = React.useMemo(() => {
      const taskslist = data?.taskList?.tasks ?? [];
      const userRole = data?.taskList?.userRole;
      switch (taskStatus) {
        case TaskStatus.Open:
          return createOpenTasksTableRows({
            taskslist,
            userRole,
            refetch: reloadAll || _refetch,
          });
        case TaskStatus.Completed:
          return createClosedTasksTableRows({ taskslist });
      }
    }, [data, taskStatus, _refetch]);

    useImperativeHandle(ref, () => ({
      reload: _refetch,
    }));

    const tabs = React.useMemo(() => {
      return [
        {
          label: OPEN_TASKS_TAB,
          content: '',
          testId: 'opened-requests-tab',
        },
        {
          label: COMPLETED_TASKS_TAB,
          content: '',
          testId: 'history-tab',
        },
      ];
    }, []);

    const handleTabSelect = React.useCallback((selected: TabData) => {
      switch (selected.label) {
        case OPEN_TASKS_TAB: {
          setTaskStatus(TaskStatus.Open);
          break;
        }
        case COMPLETED_TASKS_TAB: {
          setTaskStatus(TaskStatus.Completed);
          break;
        }
      }
    }, []);

    useEffect(() => {
      if (!user || skip) return;
      (async () => {
        try {
          setLoading(true);
          const { data, error } = await client.query<FetchTaskListQuery>({
            query: fetchTaskList,
            variables: {
              staffId: user?.userId?.split('@')?.[0],
              taskType: TaskType.AllTasks,
              taskStatus,
              businessKey,
            },
            fetchPolicy: 'no-cache',
          });
          if (error) throw error;
          setData(data);
          setError(null);
        } catch (err) {
          setData(null);
          setError(err as Error);
        } finally {
          setLoading(false);
        }
      })();
    }, [businessKey, user, refetch, client, taskStatus]);

    if (error) return <ErrorComponents.GenericErrorPage error={error} />;

    return (
      <div className="all-tasks-list" id="approval-task-section">
        <TaskListTable
          rows={visibleRows}
          head={head}
          headingText="Approval requests"
          isLoading={loading}
          searchable={searchable}
          emptyStateProps={{
            header: '',
            description: taskStatus === TaskStatus.Open ? 'No open requests to show' : 'No previous request to show',
          }}
        >
          <div className="tabs">
            <Tabs tabs={tabs} onSelect={handleTabSelect} />
          </div>
        </TaskListTable>
      </div>
    );
  }
);
AllTasksList.displayName = 'AllTasksList';

const ComponentWithDialog = withConfirmationDialog(AllTasksList as any);
const ComponentWithApollo = forwardRef<any, AllTaskListProps>((props, ref) => {
  return (
    <ApolloProvider client={abgGraphqlClient(ProxyGraphClients.APD)}>
      <ComponentWithDialog {...props} ref={ref} />
    </ApolloProvider>
  );
});
ComponentWithApollo.displayName = 'AllTasksList';

export default ComponentWithApollo;
